import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DataHub: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout: authLogout } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState('Teacher');

  useEffect(() => {
    // Beta testing mode - skip auth check, allow everyone
    console.log('🧪 Beta mode - skipping auth check');
    
    // Get name from URL if provided, otherwise use default
    const firstName = searchParams.get('firstName') || 'Beta';
    const lastName = searchParams.get('lastName') || 'Tester';
    const email = searchParams.get('email') || 'teacher@test.com';
    
    // Store user info for display
    const fullName = `${firstName} ${lastName}`;
    sessionStorage.setItem('gameToken', 'beta-tester');
    sessionStorage.setItem('gameEmail', email);
    sessionStorage.setItem('gameSub', 'true');
    sessionStorage.setItem('userName', fullName);
    setUserName(fullName);
    
    // Clean URL if parameters are present
    if (searchParams.get('token') || searchParams.get('email') || searchParams.get('sub')) {
      navigate(window.location.pathname, { replace: true });
    }
    
    // User is authorized
    setIsAuthorized(true);
    setIsLoading(false);
  }, [navigate, searchParams]);

  const handleLogout = async () => {
    try {
      // Clear auth data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('subscriptionActive');
      localStorage.removeItem('loginTimestamp');
      
      // Clear auth data from sessionStorage
      sessionStorage.removeItem('gameToken');
      sessionStorage.removeItem('gameEmail');
      sessionStorage.removeItem('gameSub');
      
      // Call auth context logout
      if (authLogout) {
        await authLogout();
      }
      
      // Redirect to marketing site
      const isSubdomain = window.location.hostname !== 'counterscripthq.com' && 
                         !window.location.hostname.includes('localhost');
      if (isSubdomain) {
        window.location.href = 'https://counterscripthq.com';
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Show loading screen while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-tactical-frame border-t-copper rounded-full animate-spin mx-auto mb-4"></div>
          <div className="animate-pulse text-white text-xl mb-4 font-display">
            Verifying Access...
          </div>
          <div className="text-gray-400 text-sm font-mono">
            Checking subscription status
          </div>
        </div>
      </div>
    );
  }

  // Show nothing if not authorized (they'll be redirected)
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-display font-bold text-white">
            COUP Data Hub
          </h1>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-yellow-400 bg-yellow-900/30 px-3 py-1 rounded">
              🧪 Beta Testing Mode
            </div>
            <Link to="/app" className="btn-ghost">
              ← Back to Dashboard
            </Link>
            <button 
              onClick={handleLogout}
              className="btn-ghost text-alert-red hover:text-red-600"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="bg-gray-900 rounded-lg p-6 mb-6">
          <p className="text-xl text-white">
            Welcome, <span className="text-green-400">{userName}</span>
          </p>
          <p className="text-gray-400 mt-2">
            Thank you for beta testing COUP!
          </p>
        </div>

        {/* Placeholder for Game Interface */}
        <div className="card-tactical">
          <div className="text-center py-20">
            <div className="w-24 h-24 border-2 border-copper flex items-center justify-center mx-auto mb-8 animate-pulse-slow">
              <svg className="w-16 h-16 text-copper" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Game Interface Loading...
            </h2>
            <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
              This is a placeholder for the COUP game interface. The actual Data Hub will be embedded here, 
              allowing you to manage factions, sessions, and track writing submissions.
            </p>
            
            <div className="bg-black border-2 border-tactical-frame p-8 max-w-3xl mx-auto text-left">
              <h3 className="text-xl font-display font-semibold text-white mb-4">
                Integration Instructions:
              </h3>
              <ul className="space-y-3 text-text-secondary">
                <li className="flex items-start">
                  <span className="text-copper mr-3">→</span>
                  <span>Replace this component with your existing Data Hub game interface</span>
                </li>
                <li className="flex items-start">
                  <span className="text-copper mr-3">→</span>
                  <span>Use an iframe, embedded React component, or full-screen game container</span>
                </li>
                <li className="flex items-start">
                  <span className="text-copper mr-3">→</span>
                  <span>Maintain the "Back to Dashboard" navigation for easy exit</span>
                </li>
                <li className="flex items-start">
                  <span className="text-copper mr-3">→</span>
                  <span>Pass user authentication context to the game for session management</span>
                </li>
              </ul>
            </div>

            <div className="mt-12">
              <p className="text-text-text-muted font-mono text-sm mb-4">
                Example iframe integration:
              </p>
              <div className="bg-tactical-panel border-2 border-tactical-frame p-4 font-mono text-sm text-text-secondary text-left max-w-2xl mx-auto overflow-x-auto">
                <code>
                  &lt;iframe<br />
                  &nbsp;&nbsp;src="https://your-game-url.com"<br />
                  &nbsp;&nbsp;className="w-full h-screen border-0"<br />
                  &nbsp;&nbsp;title="COUP Data Hub"<br />
                  &nbsp;&nbsp;allow="fullscreen"<br />
                  /&gt;
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Game Features Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="card-tactical">
            <h3 className="text-lg font-display font-semibold text-white mb-2">
              Session Management
            </h3>
            <p className="text-text-secondary text-sm">
              Create and manage classroom sessions, assign factions, and control game flow.
            </p>
          </div>
          <div className="card-tactical">
            <h3 className="text-lg font-display font-semibold text-white mb-2">
              Writing Analytics
            </h3>
            <p className="text-text-secondary text-sm">
              Track team submissions, evaluate arguments, and analyze writing quality metrics.
            </p>
          </div>
          <div className="card-tactical">
            <h3 className="text-lg font-display font-semibold text-white mb-2">
              Territory Control
            </h3>
            <p className="text-text-secondary text-sm">
              Visual map interface showing faction control based on writing strength and evidence.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataHub;
